/**
 * Currency Utility Functions
 * Handles currency formatting, conversion, and exchange rate management
 */

// Supported currencies with their symbols and formatting rules
export const CURRENCIES = {
  USD: { symbol: '$', code: 'USD', name: 'US Dollar', decimals: 2 },
  EUR: { symbol: '€', code: 'EUR', name: 'Euro', decimals: 2 },
  GBP: { symbol: '£', code: 'GBP', name: 'British Pound', decimals: 2 },
  AED: { symbol: 'د.إ', code: 'AED', name: 'UAE Dirham', decimals: 2 },
  AUD: { symbol: 'A$', code: 'AUD', name: 'Australian Dollar', decimals: 2 },
  CAD: { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar', decimals: 2 },
  INR: { symbol: '₹', code: 'INR', name: 'Indian Rupee', decimals: 0 },
};

// LocalStorage keys
const STORAGE_KEYS = {
  CURRENCY: 'selectedCurrency',
  EXCHANGE_RATES: 'exchangeRates',
  LAST_UPDATED: 'ratesLastUpdated',
};

// Default currency
export const DEFAULT_CURRENCY = 'USD';

// Exchange rate API (USD as base currency)
const EXCHANGE_RATE_API = 'https://api.exchangerate-api.com/v4/latest/USD';

// Cache duration (12 hours in milliseconds)
const CACHE_DURATION = 12 * 60 * 60 * 1000;

/**
 * Format a number as currency with proper symbol and decimals
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code (INR, USD, etc.)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currencyCode = DEFAULT_CURRENCY) => {
  if (!amount || isNaN(amount)) return `${CURRENCIES[currencyCode]?.symbol || '₹'}0`;
  
  const currency = CURRENCIES[currencyCode] || CURRENCIES.INR;
  const roundedAmount = currency.decimals === 0 
    ? Math.round(amount)
    : Number(amount.toFixed(currency.decimals));
  
  // Format with thousand separators (commas)
  const formatted = roundedAmount.toLocaleString('en-US', {
    minimumFractionDigits: currency.decimals,
    maximumFractionDigits: currency.decimals,
    useGrouping: true  // Enable thousand separators
  });
  
  return `${currency.symbol}${formatted}`;
};

/**
 * Convert amount from USD to target currency
 * @param {number} amountInUSD - Amount in US Dollars
 * @param {string} targetCurrency - Target currency code
 * @param {object} exchangeRates - Exchange rates object
 * @returns {number} Converted amount
 */
export const convertCurrency = (amountInUSD, targetCurrency, exchangeRates) => {
  if (!amountInUSD || isNaN(amountInUSD)) return 0;
  if (targetCurrency === 'USD') return amountInUSD;
  
  const rate = exchangeRates?.[targetCurrency];
  if (!rate) return amountInUSD;
  
  return amountInUSD * rate;
};

/**
 * Convert and format price in one go
 * @param {number} amountInUSD - Amount in US Dollars (base currency)
 * @param {string} targetCurrency - Target currency code
 * @param {object} exchangeRates - Exchange rates object
 * @returns {string} Formatted currency string
 */
export const convertAndFormatPrice = (amountInUSD, targetCurrency, exchangeRates) => {
  const converted = convertCurrency(amountInUSD, targetCurrency, exchangeRates);
  return formatCurrency(converted, targetCurrency);
};

/**
 * Load exchange rates from API
 * @returns {Promise<object>} Exchange rates object
 */
export const loadExchangeRates = async () => {
  try {
    const response = await fetch(EXCHANGE_RATE_API);
    if (!response.ok) throw new Error('Failed to fetch exchange rates');
    
    const data = await response.json();
    const rates = data.rates || {};
    
    // Save to localStorage with timestamp
    localStorage.setItem(STORAGE_KEYS.EXCHANGE_RATES, JSON.stringify(rates));
    localStorage.setItem(STORAGE_KEYS.LAST_UPDATED, Date.now().toString());
    
    return rates;
  } catch (error) {
    console.error('Error loading exchange rates:', error);
    // Return cached rates if available
    return getCachedExchangeRates();
  }
};

/**
 * Get cached exchange rates from localStorage
 * @returns {object|null} Cached exchange rates or null
 */
export const getCachedExchangeRates = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEYS.EXCHANGE_RATES);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Error reading cached rates:', error);
    return null;
  }
};

/**
 * Check if cached rates are still valid
 * @returns {boolean} True if cache is valid
 */
export const isCacheValid = () => {
  const lastUpdated = localStorage.getItem(STORAGE_KEYS.LAST_UPDATED);
  if (!lastUpdated) return false;
  
  const timeSinceUpdate = Date.now() - parseInt(lastUpdated, 10);
  return timeSinceUpdate < CACHE_DURATION;
};

/**
 * Get exchange rates - from cache if valid, otherwise fetch fresh
 * @returns {Promise<object>} Exchange rates object
 */
export const getExchangeRates = async () => {
  // Check if cache is valid
  if (isCacheValid()) {
    const cached = getCachedExchangeRates();
    if (cached) return cached;
  }
  
  // Fetch fresh rates
  return await loadExchangeRates();
};

/**
 * Save selected currency to localStorage
 * @param {string} currencyCode - Currency code to save
 */
export const saveCurrencyPreference = (currencyCode) => {
  try {
    localStorage.setItem(STORAGE_KEYS.CURRENCY, currencyCode);
  } catch (error) {
    console.error('Error saving currency preference:', error);
  }
};

/**
 * Get saved currency from localStorage
 * @returns {string} Saved currency code or default
 */
export const getSavedCurrency = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.CURRENCY);
    return saved && CURRENCIES[saved] ? saved : DEFAULT_CURRENCY;
  } catch (error) {
    console.error('Error reading saved currency:', error);
    return DEFAULT_CURRENCY;
  }
};

/**
 * Get currency symbol by code
 * @param {string} currencyCode - Currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
  return CURRENCIES[currencyCode]?.symbol || '₹';
};

/**
 * Get all currency options for dropdown
 * @returns {Array} Array of currency objects
 */
export const getCurrencyOptions = () => {
  return Object.values(CURRENCIES);
};
